import { useRef, useCallback, useEffect, useState } from "react";
import { MediaStreamType } from "../constants/webrtc";

interface UseStreamPlayerProps {
  onPlaybackError?: (streamType: MediaStreamType, error: any) => void;
  onAudioResumed?: () => void;
}

export const useStreamPlayer = ({
  onPlaybackError,
  onAudioResumed,
}: UseStreamPlayerProps = {}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioNeedsUserGesture = useRef(false);
  const userInteractionListenersAdded = useRef(false);
  const [isAudioPaused, setIsAudioPaused] = useState(false);

  const onUserInteraction = useCallback(async () => {
    if (audioNeedsUserGesture.current && audioRef.current?.srcObject) {
      try {
        await audioRef.current.play();
        audioNeedsUserGesture.current = false;
        removeUserInteractionListeners();
        onAudioResumed?.();
      } catch (error) {
        console.error("Failed to resume audio on user interaction:", error);
      }
    }
  }, [onAudioResumed]);

  const addUserInteractionListeners = useCallback(() => {
    if (userInteractionListenersAdded.current) return;

    const events = ["touchstart", "touchend", "mousedown", "keydown", "click"];
    events.forEach((event) => {
      document.addEventListener(event, onUserInteraction, { once: true });
    });
    userInteractionListenersAdded.current = true;
  }, [onUserInteraction]);

  const removeUserInteractionListeners = useCallback(() => {
    if (!userInteractionListenersAdded.current) return;

    const events = ["touchstart", "touchend", "mousedown", "keydown", "click"];
    events.forEach((event) => {
      document.removeEventListener(event, onUserInteraction);
    });
    userInteractionListenersAdded.current = false;
  }, [onUserInteraction]);

  useEffect(() => {
    return () => {
      removeUserInteractionListeners();
    };
  }, [removeUserInteractionListeners]);

  const playStream = async (
    stream: MediaStream,
    streamType: MediaStreamType,
  ) => {
    try {
      if (streamType === MediaStreamType.Video && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current!.play();
            console.log("Video stream playing successfully");
          } catch (error: any) {
            console.error("Video play failed:", error);
            if (error.name === "NotAllowedError") {
              console.log("User interaction required for video playback");
            }
            onPlaybackError?.(streamType, error);
          }
        };
      } else if (streamType === MediaStreamType.Audio && audioRef.current) {
        audioRef.current.srcObject = stream;
        audioRef.current.onloadedmetadata = async () => {
          try {
            await audioRef.current!.play();
            console.log("Audio stream playing successfully");
            // Clear any pending user gesture state since audio played successfully
            audioNeedsUserGesture.current = false;
          } catch (error: any) {
            console.error("Audio play failed:", error);
            if (error.name === "NotAllowedError") {
              console.log("User interaction required for audio playback");
              audioNeedsUserGesture.current = true;
              addUserInteractionListeners();
            }
            onPlaybackError?.(streamType, error);
          }
        };
        audioRef.current.load();
      }
    } catch (error) {
      console.error(`Error setting up ${streamType} stream:`, error);
      onPlaybackError?.(streamType, error);
    }
  };

  const startStream = async () => {
    // Start video if it needs user interaction
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        await videoRef.current.play();
        console.log("Video stream started successfully");
      } catch (error: any) {
        console.error("Failed to start video stream:", error);
      }
    }

    // Start audio if it needs user interaction
    if (audioRef.current && audioRef.current.srcObject) {
      try {
        audioRef.current.load();
        await audioRef.current.play();
        console.log("Audio stream started successfully");
        audioNeedsUserGesture.current = false;
        removeUserInteractionListeners();
      } catch (error: any) {
        console.error("Failed to start audio stream:", error);
        if (error.name === "NotAllowedError") {
          audioNeedsUserGesture.current = true;
          addUserInteractionListeners();
        }
      }
    }
  };

  const pauseAudio = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsAudioPaused(true);
      console.log("Audio stream paused");
    }
  }, []);

  const resumeAudioStream = useCallback(async () => {
    if (
      audioRef.current &&
      audioRef.current.paused &&
      audioRef.current.srcObject
    ) {
      try {
        await audioRef.current.play();
        setIsAudioPaused(false);
        console.log("Audio stream resumed");
        onAudioResumed?.();
      } catch (error) {
        console.error("Failed to resume audio stream:", error);
        if ((error as Error).name === "NotAllowedError") {
          audioNeedsUserGesture.current = true;
          addUserInteractionListeners();
        }
      }
    }
  }, [onAudioResumed, addUserInteractionListeners]);

  const resumeAudio = useCallback(async () => {
    return onUserInteraction();
  }, [onUserInteraction]);

  return {
    videoRef,
    audioRef,
    playStream,
    startStream,
    resumeAudio,
    pauseAudio,
    resumeAudioStream,
    isAudioRequireUserGesture: () => audioNeedsUserGesture.current,
    isAudioPaused,
  };
};
